"use client";

import { Clock, Mail, Phone, Send, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactItems = [{
  icon: Mail,
  label: "EMAIL US",
  value: "support@vpngenix.com"
}, {
  icon: Clock,
  label: "SUPPORT HOURS",
  value: "24/7 Always Online"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 border border-primary/30 rounded-lg bg-primary/5">
        <p className="text-foreground font-default text-lg mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-accent">
          Send New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="VPNGENIX" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 888 999 0000" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
              Inquiry Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full bg-transparent border-0 border-b border-border px-0 h-11 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary data-[placeholder]:text-muted-foreground/50">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-white">
              How can we help?
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your technical issue or inquiry..." rows={4} className="bg-transparent border-0 border-b border-border px-0 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="event_updates" render={({
      field
    }) => <FormItem>
            <div className="flex items-center gap-3 px-4 py-2.5 border border-border rounded-lg w-fit hover:border-primary/50 transition-colors">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
              </FormControl>
              <FormLabel className="text-sm text-foreground font-default cursor-pointer m-0">
                Subscribe to security alerts
              </FormLabel>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-lg bg-destructive/10">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default font-medium text-base">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            Submit Request
            <Send className="ml-1 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2248" id="professionalcontactgenix" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-4 sm:px-6 lg:px-12">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block w-8 h-px bg-primary" />
              <span className="font-mono text-xs tracking-[0.2em] text-muted-foreground uppercase">
                GET IN TOUCH
              </span>
            </div>

            <h1 className="font-default font-semibold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white mb-8">
              Contact
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mb-12">
              Need help with your VPN setup, billing, or security concerns? Our expert team is 
              available 24/7 to ensure your digital privacy remains uncompromised.
            </p>

            <div className="border-t border-border">
              {contactItems.map((item, i) => {
              const Icon = item.icon;
              return <div data-index={i} key={i} className="flex items-center gap-5 md:gap-8 py-6 border-b border-border group">
                    <div className="flex items-center justify-center size-10 rounded-md bg-primary/10 text-primary shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] md:text-xs tracking-[0.2em] text-muted-foreground uppercase mb-1">
                        {item.label}
                      </div>
                      <div className="text-base md:text-lg text-foreground font-default truncate">
                        {item.value}
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            <div className="flex items-center gap-3 mt-8">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-lg bg-primary" />
              </span>
              <span className="font-mono text-[10px] md:text-xs tracking-[0.18em] text-muted-foreground uppercase">
                FAST RESPONSE GUARANTEED
              </span>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-5 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative rounded-2xl border border-primary/20 shadow-2xl p-6 sm:p-10 md:p-12 backdrop-blur-xl" style={{
            background: "linear-gradient(155deg, hsl(220 12% 30% / 0.6) 0%, hsl(220 12% 28% / 0.5) 100%)"
          }}>
              <div className="absolute -top-3 right-6 flex items-center gap-2 bg-card border border-border px-3 py-1.5 rounded-md shadow">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75 animate-ping" />
                  <span className="relative inline-flex size-1.5 rounded-lg bg-primary" />
                </span>
                <span className="font-mono text-[10px] tracking-[0.18em] text-muted-foreground uppercase">
                  SECURE.FORM
                </span>
              </div>

              <div className="mb-8">
                <h2 className="font-default font-bold text-2xl md:text-3xl text-white mb-2">
                  Support Request
                </h2>
                <p className="text-sm text-white">
                  Submit your details and we will respond shortly.
                </p>
              </div>

              <FormProvider formKey="contact_form_vpn_genix" formId="contact_form_vpn_genix" sectionName="professionalcontactgenix" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              inquiry_type: "",
              message: "",
              event_updates: false
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Request" successMessage="Request received. Our team will contact you shortly." errorMessage="Submission failed. Please try again or email us.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}